import React, { useCallback, useRef, useState } from "react"
import { ReactComponent as UploadIcon } from "@ciq/shared/assets/icons/button/upload_icon.svg"
import { ACCEPT_STRING, ALLOWED_EXTENSIONS, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from "./constants"

/**
 * @typedef {Object} FileDropZoneProps
 * @property {(files: File[]) => void} onFilesSelect - Callback when valid files are selected
 * @property {(error: string) => void} [onError] - Callback when file validation fails
 * @property {boolean} [disabled=false] - Boolean indicating if drop zone is disabled
 */

/**
 * Validates file extension against allowed list
 * @param {string} filename - The filename to validate
 * @returns {boolean} True if extension is allowed
 */
export function isValidExtension(filename) {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf("."))
  return ALLOWED_EXTENSIONS.includes(ext)
}

/**
 * Drop zone component for file uploads.
 * Accepts CSV and Excel files up to 10MB.
 *
 * @param {FileDropZoneProps} props - Component props
 * @returns {React.ReactElement} The drop zone component
 */
export function FileDropZone({ onFilesSelect, onError, disabled = false }) {
  const inputRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)

  /**
   * Validates files and returns only valid ones
   * @param {FileList} fileList - The files to validate
   * @returns {File[]} Array of valid files
   */
  const validateFiles = useCallback((fileList) => {
    const validFiles = []

    for (const file of Array.from(fileList)) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        onError?.(`File "${file.name}" exceeds maximum size of ${MAX_FILE_SIZE_MB} MB`)
        continue
      }

      if (!isValidExtension(file.name)) {
        onError?.(`File "${file.name}" has invalid type. Please upload CSV or Excel files.`)
        continue
      }

      validFiles.push(file)
    }

    return validFiles
  }, [onError])

  const handleClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click()
    }
  }, [disabled])

  const handleKeyDown = useCallback((event) => {
    if (disabled) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      inputRef.current?.click()
    }
  }, [disabled])

  const handleFileChange = useCallback((event) => {
    if (disabled) return

    const files = event.target.files
    if (!files || files.length === 0) return

    // Copy files to array BEFORE resetting input (resetting clears the FileList)
    const fileArray = Array.from(files)

    // Reset input so same file can be selected again
    event.target.value = ""

    const validFiles = validateFiles(fileArray)
    if (validFiles.length > 0) {
      onFilesSelect(validFiles)
    }
  }, [disabled, onFilesSelect, validateFiles])

  const handleDragEnter = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current++
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleDrop = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    dragCounterRef.current = 0

    if (disabled) return

    const files = event.dataTransfer.files
    if (!files || files.length === 0) return

    const validFiles = validateFiles(files)
    if (validFiles.length > 0) {
      onFilesSelect(validFiles)
    }
  }, [disabled, onFilesSelect, validateFiles])

  const className = [
    "zone-upload-dropzone",
    disabled && "zone-upload-dropzone--disabled",
    isDragging && !disabled && "zone-upload-dropzone--dragging"
  ].filter(Boolean).join(" ")

  return (
    <div
      data-testid="file-drop-zone"
      className={className}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Drop zone for file upload. Drop files here or press Enter to browse."
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        data-testid="file-input"
        type="file"
        accept={ACCEPT_STRING}
        multiple
        onChange={handleFileChange}
        style={{ display: "none" }}
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className="zone-upload-dropzone__icon">
        <UploadIcon />
      </div>
      <div className="zone-upload-dropzone__text">
        <span className="zone-upload-dropzone__primary">Drop file or click to browse</span>
        <span className="zone-upload-dropzone__secondary">Drop CSV or Excel files here</span>
      </div>
    </div>
  )
}
